import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { Loading } from '@components/ui/loading/loading';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import { CheckboxComponent } from '@components/checkbox';
import { TextareaComponent } from '@components/textarea';
import { RegionDictionarySelectComponent } from '@app/pages/admin/components/region-dictionary-select/region-dictionary-select';
import { ParameterFieldSchema, parameterFieldSchema } from '@app/pages/admin/assignment-parameter-fields.util';
import { mergeProcessParameters, parameterKey, parseParameterDefaults, withoutParameter } from '@app/pages/admin/process-parameter-defaults.util';
import { ProcessParameterDefaultsResponse, ProcessParameterDefaultsService } from '@app/services/process-parameter-defaults.service';

@Component({
  selector: 'app-process-parameter-defaults',
  standalone: true,
  imports: [FormsModule, TranslateModule, DialogModule, PrimaryButton, SecondaryButton, Loading,
    MsfInput, MsfSelect, CheckboxComponent, TextareaComponent, RegionDictionarySelectComponent],
  templateUrl: './process-parameter-defaults.html',
  styleUrl: './process-parameter-defaults.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessParameterDefaultsComponent {
  private readonly api = inject(ProcessParameterDefaultsService);
  private readonly destroyRef = inject(DestroyRef);
  private pendingLoad?: Subscription;
  readonly processKey = input.required<string>();
  readonly visible = signal(false);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly fieldError = signal('');
  readonly jsonError = signal('');
  readonly response = signal<ProcessParameterDefaultsResponse | null>(null);
  readonly draft = signal<Record<string, unknown>>({});
  readonly otherJson = signal('{}');
  readonly schema = computed(() => parameterFieldSchema(this.processKey()) ?? []);
  readonly fields = computed(() => this.schema().map(field => {
    const key = parameterKey(this.draft(), field.name);
    const raw = key === undefined ? undefined : this.draft()[key];
    const selected = new Set(Array.isArray(raw) ? raw.map(String) : []);
    return { ...field, configured: key !== undefined, value: raw == null ? '' : typeof raw === 'object' ? JSON.stringify(raw) : String(raw),
      checked: raw === true, regions: [...selected],
      options: (field.options ?? []).map(option => ({ ...option, selected: selected.has(option.id) })) };
  }));
  readonly canSave = computed(() => !!this.response() && !this.loading() && !this.saving() && !this.fieldError() && !this.jsonError());

  constructor() {
    effect(() => {
      this.processKey();
      untracked(() => {
        this.pendingLoad?.unsubscribe();
        this.visible.set(false);
        this.loading.set(false);
        this.saving.set(false);
        this.error.set('');
        this.response.set(null);
      });
    });
  }

  open(): void {
    this.visible.set(true);
    this.load();
  }

  load(): void {
    this.pendingLoad?.unsubscribe();
    const key = this.processKey();
    this.loading.set(true);
    this.error.set('');
    this.response.set(null);
    this.pendingLoad = this.api.load(key).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        if (key !== this.processKey()) return;
        this.bind(response);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('PROCESS_DEFAULTS_LOAD_FAILED');
      },
    });
  }

  configure(field: ParameterFieldSchema, enabled: boolean): void {
    if (this.saving()) return;
    this.fieldError.set('');
    if (!enabled) {
      this.draft.set(withoutParameter(this.draft(), field.name));
      return;
    }
    if (parameterKey(this.draft(), field.name) !== undefined) return;
    const value = field.type === 'regions' || field.type === 'checks' ? []
      : field.type === 'checkbox' ? field.defaultValue === 'true'
        : field.type === 'number' ? Number(field.defaultValue ?? 0) : field.defaultValue ?? '';
    this.draft.set(mergeProcessParameters(this.draft(), { [field.name]: value }));
  }

  setValue(field: ParameterFieldSchema, raw: unknown): void {
    if (this.saving()) return;
    this.fieldError.set('');
    let value: unknown = raw;
    if (field.type === 'number') {
      value = raw === '' || raw === null || raw === undefined ? null : Number(raw);
      if (value !== null && !Number.isFinite(value) || field.name === 'RegionBufferKm'
        && (typeof value !== 'number' || !Number.isFinite(value) || value < 0)) {
        this.fieldError.set('FIELDSEG_REGION_BUFFER_INVALID');
        return;
      }
    } else if (field.type === 'polygon') {
      try { value = JSON.parse(String(raw)); }
      catch { this.fieldError.set('Process parameters must be a valid JSON object'); return; }
    }
    this.draft.set(mergeProcessParameters(this.draft(), { [field.name]: value }));
  }

  setCheck(field: ParameterFieldSchema, id: string, enabled: boolean): void {
    const key = parameterKey(this.draft(), field.name);
    const previous = key === undefined ? [] : this.draft()[key];
    const values = new Set(Array.isArray(previous) ? previous.map(String) : []);
    if (enabled) values.add(id); else values.delete(id);
    this.setValue(field, [...values]);
  }

  setOtherJson(json: string): void {
    if (this.saving()) return;
    this.otherJson.set(json);
    const parsed = parseParameterDefaults(json);
    if (!parsed.ok) { this.jsonError.set(parsed.error); return; }
    const known = new Set(this.schema().map(field => field.name.toLowerCase()));
    if (Object.keys(parsed.parameters).some(key => known.has(key.toLowerCase()))) {
      this.jsonError.set('PROCESS_DEFAULTS_USE_TYPED_FIELDS');
      return;
    }
    this.jsonError.set('');
    const typed = Object.fromEntries(Object.entries(this.draft()).filter(([key]) => known.has(key.toLowerCase())));
    this.draft.set(mergeProcessParameters(typed, parsed.parameters));
  }

  save(): void {
    if (!this.canSave()) return;
    this.saving.set(true);
    this.error.set('');
    const key = this.processKey();
    this.api.save(key, this.draft()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => { if (key === this.processKey()) { this.bind(response); this.saving.set(false); } },
      error: () => { if (key === this.processKey()) { this.error.set('PROCESS_DEFAULTS_SAVE_FAILED'); this.saving.set(false); } },
    });
  }

  clear(): void {
    if (this.saving() || !this.response()?.hasOverride) return;
    this.saving.set(true);
    this.error.set('');
    const key = this.processKey();
    this.api.clear(key).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => { if (key === this.processKey()) { this.bind(response); this.saving.set(false); } },
      error: () => { if (key === this.processKey()) { this.error.set('PROCESS_DEFAULTS_SAVE_FAILED'); this.saving.set(false); } },
    });
  }

  private bind(response: ProcessParameterDefaultsResponse): void {
    this.response.set(response);
    this.draft.set(structuredClone(response.parameters));
    const known = new Set(this.schema().map(field => field.name.toLowerCase()));
    this.otherJson.set(JSON.stringify(Object.fromEntries(Object.entries(response.parameters)
      .filter(([key]) => !known.has(key.toLowerCase()))), null, 2));
    this.fieldError.set('');
    this.jsonError.set('');
  }
}
